/**
 *
 * Application wide factory types.
 * Do not abuse this scope.
 *
 */
import { ApplicationState } from 'app/types-store';
import { AuthState } from 'app/types-shared';
import { STATE } from 'app/constants';
import { reducer as ShellReducers } from 'app/shell/reducers';
import { reducer as CaseReducers } from 'app/area/case/reducers';
import { reducer as FavouriteReducers } from 'app/area/favourite/reducers';
import { reducer as DashboardReducers } from 'app/area/dashboard/reducers';
import { reducer as CompanyReportReducers } from 'app/area/company-report/reducers';
import { reducer as CaseListingReducers } from 'app/area/case-report/case-listing/reducers';
import { reducer as ResponseCachingReducers } from 'app/area/shared/response-caching/reducers';
import { reducer as SelectionReportReducers } from 'app/area/reports/reducers';
import { reducer as ProfileReducers } from 'app/area/user-profile/settings/reducers';
import { reducer as AdministrationCodetableReducers } from 'app/area/codetable/reducers';
import { reducer as IndicatorFactorsReducers } from 'app/area/indicator-factors/reducers';
import { reducer as LinkCodesReducer } from 'app/area/link-codes/reducers';
import { reducer as UserDefaultValues } from 'app/area/user-profile/default-values/reducers';
import { reducer as SubscriptionsReducers } from 'app/area/user-profile/subscriptions/reducers';
import { reducer as AdministrationEditReducers } from 'app/area/administration/edit/user/reducers';
import { reducer as AdminSearchReducer } from 'app/area/administration/list/reducers';
import { reducer as AdministrationRoleEditReducers } from 'app/area/administration/edit/role/reducers';
import { reducer as AdministrationMessagesReducers } from 'app/area/messages-administration/reducers';
import { reducer as DashboardAdminReducers } from 'app/area/dashboard-administration/reducers';
import { reducer as CompanyReportsAdminReducers } from 'app/area/company-reports-administration/reducers';
import { reducer as FavouritesAdminReducers } from 'app/area/favourite-administration/reducers';
import { reducer as ImportSetsAdminReducers } from 'app/area/import-sets-administration/reducers';
import { reducer as ExternalUrlsAdminReducers } from 'app/area/external-urls-administration/reducers';
import { reducer as ExternalUrlGroupReducers } from 'app/area/external-url-groups/reducers';
import { reducer as SubscriberGroupReducers } from 'app/area/subscriber-groups/reducers';
import { reducer as ExperienceTransferReducers } from 'app/area/experience-transfer/reducers';
import { reducer as textConfigurationReducers } from 'app/area/text-configuration/reducers';
import { reducer as SearchFilterReducers } from 'app/area/search-filters/reducers';
import { reducer as FiltersReducers } from 'app/area/filters/reducers';
import { reducer as BowtieReducers } from 'app/area/bowtie/reducers';
import { reducer as SavedReportsReducers } from 'app/area/saved-reports/reducers';
import { reducer as MyDownloadReducers } from 'app/area/my-downloads/reducers';
import { reducer as MyNotificationsReducers } from 'app/area/user-profile/my-notifications/reducers';
import { reducer as ImportReducers } from 'app/area/import/reducers';
import { reducer as CodeSelectorReducer } from 'app/area/code-selector/reducers';
import { reducer as RemoteDataReducers } from 'app/infrastructure/remote-data/reducers';
import { reducer as CodetableAccessReducers } from 'app/area/shared/manage-options/reducers';
import { reducer as LinksSectionReducers } from 'app/area/links-section/reducers';
import { reducer as RiskMatrixReducers } from 'app/area/risk-matrix-administration/reducers';
import { reducer as codeMappingsReducers } from 'app/area/code-mappings/reducers';
import { reducer as importMappingsHiearchyReducers } from 'app/area/import-mappings/reducers';
import { reducer as RiskReportReducers } from 'app/area/case-report/risk-listing/reducers';
import { CaseTableViewReducer }  from 'app/area/case-report/case-table-view/reducers';
import { reducer as aiChatAssistantReducer } from "app/components/ai-chat-assistant/reducer";

/**
 * The difference in minutes (as seen from UTC) between UTC and Client's time zone
 */
const clientDateOffset = -1 * new Date().getTimezoneOffset();

/**
 * The initial state of the complete application at root url
 */
export const INITIAL_APP_STATE: ApplicationState = {
  app: {
    settings: {
      baseUrl: '',
      utcOffset: clientDateOffset,
      fallbackLocale: 'en'
    },
    authState: AuthState.NotAuthenticated,
    texts: {} as any,
    fetching: false,
    errors: [],
    messages: [],
    messagesToUser: [],
    updatingMessagesToUser: false,
    fixedHeader: true
  },
  userProfileArea: {}
};

/**
 * Whenever an action is dispatched, Redux will update each top-level application state property using
 * the reducer with the matching name. It's important that the names match exactly, and that the reducer
 * acts on the corresponding ApplicationState property type
 */
export const reducers = {
  [STATE.app]: ShellReducers,
  // There are currently no active startup reducers in startup, but text collection gets loaded by convention
  [STATE.startupArea]: (state: any = {}) => state,
  [STATE.case]: CaseReducers,
  [STATE.responseCaching]: ResponseCachingReducers,
  [STATE.favourite]: FavouriteReducers,
  [STATE.dashboard]: DashboardReducers,
  [STATE.userSettings]: ProfileReducers,
  [STATE.companyReport]: CompanyReportReducers,
  [STATE.caseListing]: CaseListingReducers,
  [STATE.selectionReport]: SelectionReportReducers,
  [STATE.filters]: FiltersReducers,
  [STATE.bowtie]: BowtieReducers,
  [STATE.administrationCodetable]: AdministrationCodetableReducers,
  [STATE.administrationSearch]: AdminSearchReducer,
  [STATE.administrationEdit]: AdministrationEditReducers,
  [STATE.administrationRoleEdit]: AdministrationRoleEditReducers,
  [STATE.administrationMessages]: AdministrationMessagesReducers,
  [STATE.dashboardAdministration]: DashboardAdminReducers,
  [STATE.companyReportAdministration]: CompanyReportsAdminReducers,
  [STATE.favouriteAdministration]: FavouritesAdminReducers,
  [STATE.importSetAdministration]: ImportSetsAdminReducers,
  [STATE.externalUrlAdministration]: ExternalUrlsAdminReducers,
  [STATE.externalUrlGroups]: ExternalUrlGroupReducers,
  [STATE.subscriberGroups]: SubscriberGroupReducers,
  [STATE.experienceTransfer]: ExperienceTransferReducers,
  [STATE.textConfiguration]: textConfigurationReducers,
  [STATE.indicatorFactors]: IndicatorFactorsReducers,
  [STATE.riskMatrix]: RiskMatrixReducers,
  [STATE.linkCodes]: LinkCodesReducer,
  [STATE.searchFilters]: SearchFilterReducers,
  [STATE.savedReport]: SavedReportsReducers,
  [STATE.myDownloads]: MyDownloadReducers,
  [STATE.import]: ImportReducers,
  [STATE.codeSelector]: CodeSelectorReducer,
  [STATE.myNotifications]: MyNotificationsReducers,
  [STATE.userDefaultValues]: UserDefaultValues,
  [STATE.subscriptions]: SubscriptionsReducers,
  [STATE.remoteData]: RemoteDataReducers,
  [STATE.codetableAccess]: CodetableAccessReducers,
  [STATE.linksSection]: LinksSectionReducers,
  [STATE.codeMappings]: codeMappingsReducers,
  [STATE.importMappings]: importMappingsHiearchyReducers,
  [STATE.riskReport]: RiskReportReducers,
  [STATE.caseTableView]: CaseTableViewReducer,
  [STATE.aiChatAssistant]: aiChatAssistantReducer
};
